/**
 * The Seed vault's small pieces on the Beds page (D69; design rev 1 §4.1 and §4.3, critique R21; tests 29 and 32):
 *   - VaultIcon: the seed-packet icon before a bed's name, drawn only where the server says is_vault (the app never
 *     names the kind), in the card, the tile and the row;
 *   - VaultEmpty: a vault with no plant growing and nothing waiting shows the garden's address card where its plants
 *     would be (SeedAddressCard: the address and Copy, or the Grow More sentence on Free), so a gardener who made the
 *     vault by hand finds the address without the profile.
 *
 * Where: LocationCard, LocationTile, LocationRow. API: GET /seed-orders/mine (VaultEmpty; the server makes the address
 * on the first read when the plan allows it). Words (en/es): locations.vault.icon.
 *
 * @module VaultBits
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { getSeedOrdersMine } from '@/shared/api/seedOrders';
import { SeedAddressCard } from './SeedAddressCard';
import { SEED_ORDERS_MINE_KEY } from './lib/seedOrderQueries';

/**
 * The seed-packet icon, named for screen readers.
 *
 * @param props.className - Its size and colour (the theme's muted text by default).
 */
export const VaultIcon: React.FC<{ className?: string }> = ({ className }) => {
  const { reactTranslate: t } = useTranslation();
  return <Package className={cn('h-4 w-4 shrink-0 text-muted-foreground', className)} role="img" aria-label={t('locations.vault.icon')} />;
};

/** The address card in an empty vault; nothing while it is read or when the read fails (the beds still work). */
export const VaultEmpty: React.FC = () => {
  const { data } = useQuery({ queryKey: SEED_ORDERS_MINE_KEY, queryFn: getSeedOrdersMine });
  if (!data) return null;
  return <div className="px-2 pb-2"><SeedAddressCard mine={data} /></div>;
};
